import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMangoApiClient } from '~/atoms/MangoApi'
import Logger from '~/services/Logger'

import { ApiError, SipConnectionInfo } from '@mangovoice/types'

const logger = new Logger('sipQueriesLogger')

interface GetSipConnectionInfoParams {
  extensionId: number
  options?: UseQueryOptions<SipConnectionInfo, ApiError>
}

// The API returns HTTP 200 with no registration servers for a misconfigured
// PBX. Returning that payload would cache it as a success for the full
// staleTime, so it is rejected instead: there is nowhere to register, and
// every consumer that reads `registration_servers` either throws or builds a
// `wss://undefined` URL.
const hasRegistrationServers = (info: SipConnectionInfo): boolean =>
  Array.isArray(info.registration_servers) &&
  info.registration_servers.length > 0

// Reported once per extension. An errored query is always stale, so the
// queryFn re-runs on every window focus (the app-wide QueryClient keeps React
// Query's `refetchOnWindowFocus` default) and on every manual retry, and each
// run would otherwise capture a Sentry event for the same provisioning row.
// Cleared on success so a PBX that breaks again is reported again.
const extensionsMissingServersReported = new Set<number>()

// Export for testing - the Set outlives individual test cases
export const _testHelpers = {
  clearMissingServerReports: () => extensionsMissingServersReported.clear(),
}

export const useGetSipConnectionInfo = ({
  extensionId,
  options,
}: GetSipConnectionInfoParams) => {
  const api = useMangoApiClient()

  const query = useQuery<SipConnectionInfo, ApiError>({
    queryKey: ['sip', extensionId],
    queryFn: async () => {
      if (extensionId === 0) return Promise.reject(undefined)

      const { ok, data } =
        await api.extensions.getSipConnectionInfo(extensionId)

      if (!ok) {
        return Promise.reject(data as ApiError)
      }
      const sipConnectionInfo = data as SipConnectionInfo

      if (!hasRegistrationServers(sipConnectionInfo)) {
        if (extensionsMissingServersReported.has(extensionId)) {
          logger.debug(
            'SIP connection info still has no registration servers',
            { extensionId },
          )
        } else {
          extensionsMissingServersReported.add(extensionId)
          // An error rather than a warning because no amount of client
          // retrying can clear it. Sentry already tags events with pbx
          // id/name/uuid, so extensionId is what pins down the offending row.
          logger.error(
            'SIP connection info has no registration servers - PBX cannot register',
            null,
            {
              extensionId,
              registrationServers:
                sipConnectionInfo.registration_servers ?? null,
            },
          )
        }
        return Promise.reject({
          type: 'contract_violation',
          message: 'SIP connection info returned no registration servers',
        } satisfies ApiError)
      }

      extensionsMissingServersReported.delete(extensionId)

      return sipConnectionInfo
    },
    ...options,
    gcTime: options?.gcTime ?? 15 * 60 * 1000,
    // Static SIP creds; keep fresh to avoid refetch churn that re-triggers
    // SIP re-registration (the flicker this PR addresses).
    staleTime: options?.staleTime ?? 15 * 60 * 1000,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  return {
    ...query,
  }
}
